import { useState } from "react";
import {
  X, CheckCircle2, ChevronRight, ExternalLink, ArrowLeft,
} from "lucide-react";
import { Link } from "react-router-dom";
import Breadcrumbs from "./Breadcrumbs";
import { integrations, getCategoryById } from "../data/integrations";

import { useTranslation } from 'react-i18next';
export function ConfigModal({ integration, onClose, onConnect }) {
  const [formData, setFormData] = useState({});
  const [connecting, setConnecting] = useState(false);

  if (!integration) return null;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.id]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setConnecting(true);
    // Simulate API call
    await new Promise((r) => setTimeout(r, 1200));
    setConnecting(false);
    if (onConnect) onConnect(integration.id, formData);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-lg bg-zinc-950 border border-zinc-800 rounded-xl shadow-2xl shadow-black/50 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-800">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{
                backgroundColor: `${integration.brandColor}15`,
                border: `1px solid ${integration.brandColor}30`,
              }}
            >
              <span className="text-lg font-display font-bold" style={{ color: integration.brandColor }}>
                {integration.name.charAt(0)}
              </span>
            </div>
            <div>
              <h2 className="font-display text-lg uppercase text-white">
                Connect {integration.name}
              </h2>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{t('integration_detail_page.enter_your_credentials_below')}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-zinc-500 hover:text-zinc-300 transition-colors p-1"
            aria-label={t('integration_detail_page.close')}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-4">
            {integration.configFields.map((field) => (
              <div key={field.id}>
                <label
                  htmlFor={field.id}
                  className="block font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 mb-2"
                >
                  {field.label}
                </label>
                <input
                  id={field.id}
                  type={field.type || "text"}
                  placeholder={field.placeholder}
                  value={formData[field.id] || ""}
                  onChange={handleChange}
                  required
                  className="w-full bg-zinc-900 border border-zinc-800 rounded-lg px-4 py-2.5 text-sm text-zinc-200 placeholder:text-zinc-600 focus:outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/20 transition-colors font-mono"
                />
              </div>
            ))}
          </div>

          <div className="mt-6 p-4 border border-zinc-800 rounded-lg bg-zinc-900/50">
            <p className="text-[10px] text-zinc-500 font-mono leading-relaxed">
              Your credentials are encrypted and stored securely. We never share
              your API keys with third parties. You can disconnect at any time.
            </p>
          </div>

          <div className="mt-6 flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 border border-zinc-700 hover:border-zinc-500 text-zinc-300 px-4 py-3 font-mono text-[10px] uppercase tracking-wider transition-colors"
            >{t('integration_detail_page.cancel')}</button>
            <button
              type="submit"
              disabled={connecting}
              className="flex-1 bg-amber-500 hover:bg-amber-400 disabled:opacity-50 disabled:cursor-not-allowed text-black px-4 py-3 font-bold uppercase tracking-wider text-[10px] transition-colors flex items-center justify-center gap-2"
            >
              {connecting ? (
                <>
                  <div className="w-3.5 h-3.5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                  Connecting...
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-3.5 h-3.5" /> Connect
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function IntegrationDetail({ integration, onConnect, onDisconnect, isConnected }) {
  const { t } = useTranslation();
  const [showConfig, setShowConfig] = useState(false);
  const category = getCategoryById(integration.category);

  const handleConnect = () => {
    setShowConfig(true);
  };

  const handleConfigConnect = (id) => {
    if (onConnect) onConnect(id);
    setShowConfig(false);
  };

  const handleDisconnect = () => {
    if (onDisconnect) onDisconnect(integration.id);
  };

  const breadcrumbItems = [
    { label: "Home", path: "/" },
    { label: "Marketplace", path: "/marketplace" },
    { label: "Integrations", path: "/marketplace/integrations" },
    { label: integration.name },
  ];

  const brandColor = integration.brandColor || "#f59e0b";

  return (
    <>
      <Breadcrumbs items={breadcrumbItems} />

      <main className="max-w-7xl mx-auto px-6 lg:px-12 pb-20">
        {/* Back link */}
        <Link
          to="/marketplace/integrations"
          className="inline-flex items-center gap-2 text-zinc-400 hover:text-amber-500 font-mono text-[10px] uppercase tracking-[0.2em] mb-8 transition-colors"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to Integrations
        </Link>

        {/* Hero section */}
        <div className="flex flex-col md:flex-row md:items-start gap-8 mb-16">
          <div
            className="w-20 h-20 rounded-xl flex items-center justify-center shrink-0"
            style={{
              backgroundColor: `${brandColor}15`,
              border: `1px solid ${brandColor}30`,
            }}
          >
            <span className="text-3xl font-display font-bold" style={{ color: brandColor }}>
              {integration.name.charAt(0)}
            </span>
          </div>

          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 border border-zinc-800 px-2.5 py-1">
                {category?.icon} {category?.name}
              </span>
              {isConnected && (
                <span className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-amber-500 border border-amber-500/30 px-2.5 py-1">
                  <CheckCircle2 className="w-3 h-3" /> Connected
                </span>
              )}
            </div>

            <h1 className="text-4xl md:text-5xl font-display font-bold text-white mb-4">
              {integration.name}
            </h1>

            <p className="text-lg text-zinc-400 leading-relaxed max-w-2xl mb-8">
              {integration.description}
            </p>

            <div className="flex flex-wrap gap-3">
              {isConnected ? (
                <button
                  onClick={handleDisconnect}
                  className="border border-red-500/20 hover:border-red-500 text-red-400 hover:text-red-300 px-6 py-3 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors"
                >{t('integration_detail_page.disconnect')}</button>
              ) : (
                <button
                  onClick={handleConnect}
                  className="bg-amber-500 hover:bg-amber-400 text-black px-8 py-3.5 font-bold uppercase tracking-wider text-sm transition-colors"
                >
                  Connect {integration.name}
                </button>
              )}
              <a
                href="#features"
                className="border border-zinc-700 hover:border-zinc-500 text-zinc-300 px-6 py-3.5 font-mono uppercase tracking-wider text-[10px] transition-colors flex items-center gap-2"
              >
                <ExternalLink className="w-3.5 h-3.5" /> View Features
              </a>
            </div>
          </div>
        </div>

        {/* Features section */}
        <section id="features" className="mb-16 scroll-mt-24">
          <h2 className="text-2xl font-display font-bold text-white mb-2">{t('integration_detail_page.features')}</h2>
          <p className="text-zinc-500 text-sm mb-8">
            What you get when you connect {integration.name}
          </p>

          <div className="grid sm:grid-cols-2 gap-4">
            {integration.features.map((feature, idx) => (
              <div
                key={idx}
                className="flex items-start gap-3 border border-zinc-800 rounded-lg p-5 hover:border-zinc-700 transition-colors"
              >
                <div
                  className="w-6 h-6 rounded-full flex items-center justify-center shrink-0 mt-0.5"
                  style={{ backgroundColor: `${brandColor}20` }}
                >
                  <ChevronRight className="w-3 h-3" style={{ color: brandColor }} />
                </div>
                <span className="text-sm text-zinc-300 leading-relaxed">{feature}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Setup guide */}
        <section className="mb-16">
          <h2 className="text-2xl font-display font-bold text-white mb-2">{t('integration_detail_page.setup_guide')}</h2>
          <p className="text-zinc-500 text-sm mb-8">
            Follow these steps to connect {integration.name}
          </p>

          <div className="space-y-3">
            {integration.setupSteps.map((step, idx) => (
              <div
                key={idx}
                className="flex items-start gap-4 border border-zinc-800 rounded-lg p-5"
              >
                <span className="font-display text-lg text-zinc-600 shrink-0 w-8">
                  {String(idx + 1).padStart(2, "0")}
                </span>
                <span className="text-sm text-zinc-300 leading-relaxed">{step}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Screenshots mock */}
        <section className="mb-16">
          <h2 className="text-2xl font-display font-bold text-white mb-2">{t('integration_detail_page.preview')}</h2>
          <p className="text-zinc-500 text-sm mb-8">{t('integration_detail_page.how_the_integration_looks_inside_max_os_cloud')}</p>

          <div className="border border-zinc-800 rounded-xl overflow-hidden bg-zinc-950">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-zinc-800 bg-zinc-900/50">
              <div className="w-3 h-3 rounded-full bg-red-500/60" />
              <div className="w-3 h-3 rounded-full bg-yellow-500/60" />
              <div className="w-3 h-3 rounded-full bg-green-500/60" />
              <span className="ml-3 font-mono text-[10px] text-zinc-500">
                {integration.name} — Integration Preview
              </span>
            </div>
            <div className="p-8 min-h-[200px] flex items-center justify-center">
              <div className="text-center">
                <div
                  className="w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4"
                  style={{
                    backgroundColor: `${brandColor}15`,
                    border: `1px solid ${brandColor}30`,
                  }}
                >
                  <span className="text-2xl font-display font-bold" style={{ color: brandColor }}>
                    {integration.name.charAt(0)}
                  </span>
                </div>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">
                  {integration.name}
                </p>
                <p className="text-sm text-zinc-400">
                  {isConnected ? "Connected and syncing" : "Connect to get started"}
                </p>
                {isConnected && (
                  <div className="flex items-center justify-center gap-1.5 mt-2">
                    <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                    <span className="font-mono text-[10px] text-emerald-500">{t('integration_detail_page.active')}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Related integrations */}
        <section>
          <h2 className="text-2xl font-display font-bold text-white mb-6">{t('integration_detail_page.related_integrations')}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {integrations
              .filter((i) => i.category === integration.category && i.id !== integration.id)
              .slice(0, 3)
              .map((rel) => (
                <Link
                  key={rel.id}
                  to={`/marketplace/integrations/${rel.id}`}
                  className="border border-zinc-800 rounded-lg p-5 hover:border-zinc-700 transition-colors group"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center"
                      style={{
                        backgroundColor: `${rel.brandColor}15`,
                        border: `1px solid ${rel.brandColor}30`,
                      }}
                    >
                      <span className="text-sm font-display font-bold" style={{ color: rel.brandColor }}>
                        {rel.name.charAt(0)}
                      </span>
                    </div>
                    <span className="font-display text-sm uppercase text-white group-hover:text-amber-500 transition-colors">
                      {rel.name}
                    </span>
                  </div>
                  <p className="text-xs text-zinc-500">{rel.tagline}</p>
                </Link>
              ))}
          </div>
        </section>
      </main>

      {/* Config Modal */}
      {showConfig && (
        <ConfigModal
          integration={integration}
          onClose={() => setShowConfig(false)}
          onConnect={handleConfigConnect}
        />
      )}
    </>
  );
}
